import { addWeeks, format, startOfWeek, sub } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AnalyticsEvent, trackClick } from '@bonusly/analytics';
import { Box, HStack, Text, Tooltip, VStack } from '@bonusly/design-system';

import { Emoji, getMoodColor } from '../../../../components/check-ins/emoji';
import { CheckIn } from '../../../../schemas/check-in';
import { CheckInOverTimeGrouped, TimePeriod } from './types';

interface MoodChartProps {
	selectedPeriod: TimePeriod;
	checkIns: CheckIn[];
	onColumnClick?: (month: string) => void;
	selectedMonth?: string;
}

// Helper function to create month key in YYYY-MM format
const createMonthKey = (date: Date): string => {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

export function MoodChart({ selectedPeriod, checkIns, onColumnClick, selectedMonth }: MoodChartProps) {
	const currentMonth = new Date().getMonth();
	const [isHovered, setIsHovered] = useState<string | null>(null);
	const [isClicked, setIsClicked] = useState<string | null>(null);
	const [animatedBars, setAnimatedBars] = useState<Set<number>>(new Set());
	const clickedTimeoutRef = useRef<any>(null);

	const graphColumnsNum = useMemo(() => {
		if (selectedPeriod === '12w') {
			return 6; // 6 bi-weekly periods for 12 weeks
		}
		if (selectedPeriod === '6m') {
			return 6;
		}
		if (selectedPeriod === 'ytd') {
			return currentMonth + 1;
		}
		return 12;
	}, [currentMonth, selectedPeriod]);

	const chartData: CheckInOverTimeGrouped[] = useMemo(() => {
		if (selectedPeriod === '12w') {
			// For 6 weeks, group data bi-weekly (every 2 weeks)
			const seenMonths = new Set<string>();
			return new Array(graphColumnsNum).fill(0).map((_, index) => {
				const weeksBack = (graphColumnsNum - index - 1) * 2; // 0, 2, 4 weeks back
				const periodStart = startOfWeek(sub(new Date(), { weeks: weeksBack + 1 }));
				const periodEnd = addWeeks(periodStart, 2);

				// Create a unique key for this bi-weekly period
				const periodKey = `${format(periodStart, 'yyyy-MM-dd')}_${format(periodEnd, 'yyyy-MM-dd')}`;
				// const periodName = `${format(periodStart, 'MMM d')}-${format(periodEnd, 'd')}`;

				// Show month name only for first occurrence of each month
				const currentMonth = format(periodStart, 'MMM');
				const periodName = seenMonths.has(currentMonth) ? '' : currentMonth;
				seenMonths.add(currentMonth);
				const periodLabel = `${format(periodStart, 'MMM d')}-${format(periodEnd, 'd')}, ${periodStart.getFullYear()}`;

				const periodData = checkIns.filter((c) => {
					const checkInDate = new Date(c.createdAt);
					return checkInDate >= periodStart && checkInDate < periodEnd;
				});

				const averageScore = periodData.reduce((sum, checkIn) => sum + checkIn.score, 0) / periodData.length;
				const highScore = Math.max(...periodData.map((checkIn) => checkIn.score));
				const lowScore = Math.min(...periodData.map((checkIn) => checkIn.score));

				return {
					month: periodKey, // Using period key instead of month
					year: periodStart.getFullYear(),
					monthName: periodName,
					periodLabel,
					checkIns: periodData,
					averageScore: periodData.length > 0 ? averageScore : 0,
					highScore: periodData.length > 0 ? highScore : 0,
					lowScore: periodData.length > 0 ? lowScore : 0,
				};
			});
		}

		// Original monthly grouping for other periods
		return new Array(graphColumnsNum).fill(0).map((_, index) => {
			const date = sub(new Date(), { months: graphColumnsNum - index - 1 });
			const monthName = format(date, 'MMM');
			const monthKey = createMonthKey(date);
			const monthData = checkIns.filter((c) => {
				const checkInMonthKey = createMonthKey(new Date(c.createdAt));
				return checkInMonthKey === monthKey;
			});
			const averageScore = monthData.reduce((sum, checkIn) => sum + checkIn.score, 0) / monthData.length;
			const highScore = Math.max(...monthData.map((checkIn) => checkIn.score));
			const lowScore = Math.min(...monthData.map((checkIn) => checkIn.score));
			return {
				month: monthKey,
				year: date.getFullYear(),
				monthName,
				periodLabel: `${monthName} ${date.getFullYear()}`,
				checkIns: monthData,
				averageScore: monthData.length > 0 ? averageScore : 0,
				highScore: monthData.length > 0 ? highScore : 0,
				lowScore: monthData.length > 0 ? lowScore : 0,
			};
		});
	}, [graphColumnsNum, checkIns, selectedPeriod]);

	// Reset animations when data changes and trigger sequential animation
	useEffect(() => {
		setAnimatedBars(new Set());
		const timeouts: any[] = [];
		chartData.forEach((_, index) => {
			const timeout = setTimeout(() => {
				setAnimatedBars((prev) => new Set([...prev, index]));
			}, index * 100); // 100ms delay between each bar
			timeouts.push(timeout);
		});
		return () => {
			timeouts.forEach((timeout) => clearTimeout(timeout));
		};
	}, [chartData]);

	// clicked timeout is used to avoid z-index issues
	const onClick = (month: string) => {
		onColumnClick?.(month);
		setIsClicked(month);
		if (clickedTimeoutRef.current) {
			clearTimeout(clickedTimeoutRef.current);
		}
		clickedTimeoutRef.current = setTimeout(() => {
			setIsClicked(null);
		}, 1000);
	};

	// some magical constants to handle sizes when the number of bars is more than 6
	const MIN_NUM_BARS = graphColumnsNum < 6 ? 6 : graphColumnsNum;
	const BAR_HEIGHT = 128; // px
	const BASE_SIZE = 24 / MIN_NUM_BARS; // px min 6 bars
	const ICON_SIZE = BASE_SIZE + BASE_SIZE * 6; // px
	const BAR_WIDTH = ICON_SIZE + BASE_SIZE * 2.5; // px
	const HAS_MORE_THAN_7_BARS = graphColumnsNum > 7;
	const HAS_MORE_THAN_9_BARS = graphColumnsNum > 9;

	return (
		<Box position="relative" p={4} bgColor="bg.subtle" borderRadius="md">
			<GridLines />

			<HStack zIndex={1}>
				<HStack zIndex={1} flex="1" spacing={0}>
					{chartData.map((month, index) => {
						// Calculate bar height based on average score (1-5 scale)
						const normalizedScore = (month.averageScore - 1) / 4;
						const normalizedMinScore = (month.lowScore - 1) / 4;
						const normalizedMaxScore = (month.highScore - 1) / 4;

						const hasSelectedMonth = selectedMonth !== undefined;
						const isSelected = month.month === selectedMonth;
						const hasValue = month.checkIns.length > 0;

						const barBottom = normalizedMinScore * BAR_HEIGHT - ICON_SIZE / 2;
						const barHeight = (normalizedMaxScore - normalizedMinScore) * BAR_HEIGHT + ICON_SIZE + BASE_SIZE * 2.5;
						const iconBottom = (normalizedScore - normalizedMinScore) * BAR_HEIGHT + ICON_SIZE + BASE_SIZE;
						const monthName = graphColumnsNum > 6 ? month.monthName.slice(0, 1) : month.monthName;
						const isHoveredOrSelected = isHovered === month.month || isSelected;

						const isAnimated = animatedBars.has(index);
						const animatedBarBottom = isAnimated ? barBottom : ICON_SIZE; // is the height of the emoji
						const animatedBarHeight = isAnimated ? barHeight : ICON_SIZE;
						const animatedIconBottom = isAnimated ? iconBottom : ICON_SIZE;
						const animatedOpacity = isAnimated ? 1 : 0;

						const bgColor = getMoodColor(month.averageScore);

						return (
							<VStack
								alignItems="center"
								cursor="pointer"
								flex="1"
								h="100%"
								key={month.month}
								onClick={() => onClick(month.month)}
								onMouseEnter={() => setIsHovered(month.month)}
								onMouseLeave={() => setIsHovered(null)}
								opacity={hasSelectedMonth && !isHoveredOrSelected ? 0.7 : animatedOpacity}
								spacing={0}
								transition="opacity 0.3s ease-in-out"
								w="100%"
							>
								<Box h={BAR_HEIGHT} mb={4} mt={8} display="flex" alignItems="flex-end">
									{hasValue && (
										<Tooltip
											label={
												<VStack spacing={1} align="start">
													<Text fontSize="sm" fontWeight="semibold">
														{month.periodLabel}
													</Text>
													<Text fontSize="xs">High: {month.highScore.toFixed(1)}</Text>
													<Text fontSize="xs">Low: {month.lowScore.toFixed(1)}</Text>
													<Text fontSize="xs">Average: {month.averageScore.toFixed(1)}</Text>
												</VStack>
											}
											placement="top"
											// is hovered or clicked by a second to avoid z-index issues
											isOpen={isHovered === month.month || isClicked === month.month}
										>
											<Box
												bgColor="transparent"
												borderColor={isSelected ? 'text.interactive' : 'transparent'}
												borderRadius="full"
												borderWidth={HAS_MORE_THAN_9_BARS ? '1px' : '2px'}
												bottom={`${animatedBarBottom}px`}
												h={`${animatedBarHeight}px`}
												p={HAS_MORE_THAN_7_BARS ? '0.5px' : '1px'}
												position="relative"
												transition="height 0.5s ease-in-out, bottom 0.5s ease-in-out, border-color 0.15s ease-in-out"
												w={`${BAR_WIDTH}px`}
												zIndex={0}
												{...trackClick(AnalyticsEvent.CheckIns.OverTimeChartColumnSelected, {
													month: month.month,
													averageScore: month.averageScore,
												})}
											>
												<Box h="full" w="full" bgColor={bgColor} borderRadius="full" opacity={0.25} />

												{/* Emoji */}
												<Box
													bottom={`${animatedIconBottom}px`}
													left="50%"
													transform="translateX(-50%)"
													pointerEvents="none"
													position="relative"
													zIndex={2}
													p={HAS_MORE_THAN_7_BARS ? '1px' : 0.5}
													transition="bottom 0.5s ease-in-out"
												>
													<Emoji
														shadow={isHoveredOrSelected ? 'md' : 'none'}
														value={month.averageScore}
														h={`${ICON_SIZE}px`}
														w={`${ICON_SIZE}px`}
														transform={isHoveredOrSelected ? 'scale(1.4)' : 'scale(1)'}
														showScore={false}
														transition="transform 0.5s ease-in-out"
													/>
												</Box>
											</Box>
										</Tooltip>
									)}
								</Box>
								<Text fontSize="sm" color="text.softest" fontWeight="medium" h={5} textAlign="center">
									{monthName}
								</Text>
							</VStack>
						);
					})}
				</HStack>

				<YAxisLabels />
			</HStack>
		</Box>
	);
}

// Background grid lines component
function GridLines() {
	return (
		<Box left={4} pointerEvents="none" right={12} top={-1} zIndex={0} bottom={5} position="absolute">
			{[1, 2, 3, 4, 5].map((lineIndex) => (
				<Box
					bg="#6B748529"
					height="1px"
					key={lineIndex}
					left={0}
					position="absolute"
					right={0}
					top={`${(lineIndex / 6) * 100}%`}
					transform="translateY(-0.5px)"
				/>
			))}
		</Box>
	);
}

// Y-axis labels
function YAxisLabels() {
	return (
		<VStack w="5" h="100%" gap={3.5} pb={5}>
			{[5, 4, 3, 2, 1].map((lineIndex) => (
				<Text key={lineIndex} fontSize="sm" color="text.softest" fontWeight="medium">
					{lineIndex}
				</Text>
			))}
		</VStack>
	);
}
